import type { EndpointRequest } from '@iconik-io/iconik-api/src/client/request';
import { isNotFoundError } from '@iconik-io/iconik-api/src/utils/is_not_found_error';

import { CancelUploadError } from '../../errors';
import type { EntitiesLifecycleContext } from './entities_lifecycle';

/**
 * Fails the cancel, so that the caller finds out the upload could not be
 * cleaned up rather than being left to assume it was.
 */
export function failCancel(error: Error): never {
  throw new CancelUploadError(error);
}

/**
 * Lets an entity that is already gone pass: teardown asked for it not to exist,
 * and it does not. Anything else is rethrown for failCancel to wrap.
 */
export function ignoreAlreadyDeleted(error: unknown): void {
  if (isNotFoundError(error)) {
    return;
  }
  throw error;
}

/**
 * Issues one of the deletes that undo an upload.
 *
 * These requests carry no abort signal on purpose: teardown runs after the
 * upload has been aborted, which has already aborted every signal in the abort
 * list, so a signal taken from it would cancel the delete the moment it is
 * issued.
 */
export async function deleteDuringTeardown<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
  request: EndpointRequest,
): Promise<void> {
  await context.apiClient
    .request(request, {})
    .catch(ignoreAlreadyDeleted)
    .catch(failCancel);
}
